import { useState } from 'react';
import Sidebar from '../dashboard/components/Sidebar';

const Misc = () => {
  const [showCostCalculator, setShowCostCalculator] = useState(false);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [calculatorData, setCalculatorData] = useState({
    projectType: 'residential',
    squareFootage: '',
    floors: 1,
    bathrooms: 1,
    bedrooms: 1,
    finishLevel: 'standard',
    location: '',
    additionalFeatures: {
      garage: false,
      deck: false,
      basement: false,
      fireplace: false,
      pool: false,
      landscaping: false
    }
  });
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [meetingData, setMeetingData] = useState({
    client: '',
    meetingType: 'consultation',
    title: '',
    date: '',
    time: '',
    duration: '60',
    location: '',
    meetingMethod: 'in-person',
    agenda: '',
    attendees: '',
    project: '',
    priority: 'medium'
  });

  const handleOpenCostCalculator = () => {
    setShowCostCalculator(true);
  };

  const handleCloseCostCalculator = () => {
    setShowCostCalculator(false);
    setCalculationResult(null);
    setCalculatorData({
      projectType: 'residential',
      squareFootage: '',
      floors: 1,
      bathrooms: 1,
      bedrooms: 1,
      finishLevel: 'standard',
      location: '',
      additionalFeatures: {
        garage: false,
        deck: false,
        basement: false,
        fireplace: false,
        pool: false,
        landscaping: false
      }
    });
  };

  const handleOpenScheduleMeeting = () => {
    setShowScheduleMeeting(true);
  };

  const handleCloseScheduleMeeting = () => {
    setShowScheduleMeeting(false);
    setMeetingData({
      client: '',
      meetingType: 'consultation',
      title: '',
      date: '',
      time: '',
      duration: '60',
      location: '',
      meetingMethod: 'in-person',
      agenda: '',
      attendees: '',
      project: '',
      priority: 'medium'
    });
  };

  const handleSaveMeeting = () => {
    if (!meetingData.client || !meetingData.title || !meetingData.date || !meetingData.time) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Here you would typically save to your backend
    console.log('Meeting scheduled:', meetingData);
    alert('Meeting scheduled successfully!');
    handleCloseScheduleMeeting();
  };

  const calculateCost = () => {
    const sqft = parseFloat(calculatorData.squareFootage);
    if (!sqft || sqft <= 0) return;

    // Base cost per square foot based on project type and finish level
    const baseCosts = {
      residential: {
        basic: 120,
        standard: 150,
        premium: 200,
        luxury: 300
      },
      commercial: {
        basic: 100,
        standard: 130,
        premium: 180,
        luxury: 250
      },
      renovation: {
        basic: 80,
        standard: 110,
        premium: 150,
        luxury: 220
      }
    };

    let baseCost = baseCosts[calculatorData.projectType as keyof typeof baseCosts][calculatorData.finishLevel as keyof typeof baseCosts.residential];
    
    // Adjustments for additional features
    let additionalCosts = 0;
    if (calculatorData.additionalFeatures.garage) additionalCosts += sqft * 15;
    if (calculatorData.additionalFeatures.deck) additionalCosts += sqft * 8;
    if (calculatorData.additionalFeatures.basement) additionalCosts += sqft * 25;
    if (calculatorData.additionalFeatures.fireplace) additionalCosts += 8000;
    if (calculatorData.additionalFeatures.pool) additionalCosts += 50000;
    if (calculatorData.additionalFeatures.landscaping) additionalCosts += sqft * 5;

    // Floor multiplier
    const floorMultiplier = calculatorData.floors > 1 ? 1 + (calculatorData.floors - 1) * 0.1 : 1;
    
    const totalBaseCost = sqft * baseCost * floorMultiplier;
    const totalCost = totalBaseCost + additionalCosts;
    
    // Cost breakdown
    const breakdown = {
      baseCost: totalBaseCost,
      additionalFeatures: additionalCosts,
      total: totalCost,
      costPerSqft: totalCost / sqft,
      laborCost: totalCost * 0.4,
      materialCost: totalCost * 0.45,
      overhead: totalCost * 0.15
    };

    setCalculationResult(breakdown);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar onScheduleMeeting={handleOpenScheduleMeeting} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Miscellaneous Tools</h1>
              <p className="text-sm text-gray-600 mt-1">Additional utilities and calculators for your projects</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Cost Calculator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-calculator-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cost Calculator</h3>
                  <p className="text-sm text-gray-600">Estimate project costs</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Calculate construction costs based on square footage, materials, and project specifications.</p>
              <button 
                onClick={handleOpenCostCalculator}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap"
              >
                Open Tool
              </button>
            </div>

            {/* Material Estimator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-hammer-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Material Estimator</h3>
                  <p className="text-sm text-gray-600">Calculate material needs</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Estimate quantities of materials needed for different types of construction projects.</p>
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                Open Tool
              </button>
            </div>

            {/* Unit Converter */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-exchange-line text-purple-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Unit Converter</h3>
                  <p className="text-sm text-gray-600">Convert measurements</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Convert between different units of measurement commonly used in construction.</p>
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                Open Tool
              </button>
            </div>

            {/* Area Calculator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-ruler-line text-orange-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Area Calculator</h3>
                  <p className="text-sm text-gray-600">Calculate areas and volumes</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Calculate areas and volumes for various geometric shapes and construction elements.</p>
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                Open Tool
              </button>
            </div>

            {/* Load Calculator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-scales-3-line text-red-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Load Calculator</h3>
                  <p className="text-sm text-gray-600">Calculate structural loads</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Calculate structural loads and weight distributions for construction planning.</p>
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                Open Tool
              </button>
            </div>

            {/* Timeline Planner */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-time-line text-indigo-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Timeline Planner</h3>
                  <p className="text-sm text-gray-600">Plan project timelines</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Create and manage project timelines with milestone tracking and dependencies.</p>
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 whitespace-nowrap">
                Open Tool
              </button>
            </div>
          </div>

          {/* Quick Reference Section */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Reference</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Building Codes */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Building Codes</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ceiling Height (Residential)</span>
                    <span className="font-medium">8' minimum</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stair Riser Height</span>
                    <span className="font-medium">7.75" maximum</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stair Tread Depth</span>
                    <span className="font-medium">10" minimum</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Handrail Height</span>
                    <span className="font-medium">34"-38"</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Outlet Spacing</span>
                    <span className="font-medium">12' maximum</span>
                  </div>
                </div>
              </div>

              {/* Material Weights */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Material Weights (per sq ft)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Concrete (4" thick)</span>
                    <span className="font-medium">50 lbs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Drywall (1/2")</span>
                    <span className="font-medium">2.2 lbs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plywood (3/4")</span>
                    <span className="font-medium">2.3 lbs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Asphalt Shingles</span>
                    <span className="font-medium">2.5 lbs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ceramic Tile</span>
                    <span className="font-medium">4 lbs</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Meeting Modal */}
      {showScheduleMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Schedule Meeting</h3>
              <button
                onClick={handleCloseScheduleMeeting}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      value={meetingData.client}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter client name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Type
                    </label>
                    <select
                      value={meetingData.meetingType}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, meetingType: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="consultation">Initial Consultation</option>
                      <option value="site-visit">Site Visit</option>
                      <option value="design-review">Design Review</option>
                      <option value="progress-update">Progress Update</option>
                      <option value="final-walkthrough">Final Walkthrough</option>
                      <option value="contract-signing">Contract Signing</option>
                      <option value="follow-up">Follow-up Meeting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Title *
                    </label>
                    <input
                      type="text"
                      value={meetingData.title}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter meeting title"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={meetingData.date}
                        onChange={(e) => setMeetingData(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Time *
                      </label>
                      <input
                        type="time"
                        value={meetingData.time}
                        onChange={(e) => setMeetingData(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <select
                      value={meetingData.duration}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                      <option value="180">3 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Method
                    </label>
                    <select
                      value={meetingData.meetingMethod}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, meetingMethod: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="in-person">In Person</option>
                      <option value="video-call">Video Call</option>
                      <option value="phone-call">Phone Call</option>
                      <option value="site-visit">On-Site Visit</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location/Address
                    </label>
                    <input
                      type="text"
                      value={meetingData.location}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter meeting location or video call link"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Related Project
                    </label>
                    <select
                      value={meetingData.project}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, project: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a project (optional)</option>
                      <option value="modern-kitchen-remodel">Modern Kitchen Remodel</option>
                      <option value="bathroom-renovation">Bathroom Renovation</option>
                      <option value="deck-construction">Deck Construction</option>
                      <option value="basement-finishing">Basement Finishing</option>
                      <option value="home-addition">Home Addition</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      value={meetingData.priority}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Attendees
                    </label>
                    <input
                      type="text"
                      value={meetingData.attendees}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, attendees: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter additional attendee names (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Agenda
                    </label>
                    <textarea
                      value={meetingData.agenda}
                      onChange={(e) => setMeetingData(prev => ({ ...prev, agenda: e.target.value }))}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="Enter meeting agenda, topics to discuss, or notes..."
                      maxLength={500}
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {meetingData.agenda.length}/500 characters
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCloseScheduleMeeting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMeeting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                >
                  Schedule Meeting
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Calculator Modal */}
      {showCostCalculator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Construction Cost Calculator</h3>
              <button
                onClick={handleCloseCostCalculator}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500 text-xl"></i>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input Form */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Type
                    </label>
                    <select
                      value={calculatorData.projectType}
                      onChange={(e) => setCalculatorData(prev => ({ ...prev, projectType: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="renovation">Renovation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Square Footage *
                    </label>
                    <input
                      type="number"
                      value={calculatorData.squareFootage}
                      onChange={(e) => setCalculatorData(prev => ({ ...prev, squareFootage: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter total square footage"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Floors
                      </label>
                      <input
                        type="number"
                        value={calculatorData.floors}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, floors: parseInt(e.target.value) || 1 }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bedrooms
                      </label>
                      <input
                        type="number"
                        value={calculatorData.bedrooms}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, bedrooms: parseInt(e.target.value) || 1 }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bathrooms
                      </label>
                      <input
                        type="number"
                        value={calculatorData.bathrooms}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, bathrooms: parseInt(e.target.value) || 1 }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Finish Level
                    </label>
                    <select
                      value={calculatorData.finishLevel}
                      onChange={(e) => setCalculatorData(prev => ({ ...prev, finishLevel: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="luxury">Luxury</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location (Optional)
                    </label>
                    <input
                      type="text"
                      value={calculatorData.location}
                      onChange={(e) => setCalculatorData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City, State"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Additional Features
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(calculatorData.additionalFeatures).map(([feature, checked]) => (
                        <label key={feature} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setCalculatorData(prev => ({
                              ...prev,
                              additionalFeatures: {
                                ...prev.additionalFeatures,
                                [feature]: e.target.checked
                              }
                            }))}
                            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={calculateCost}
                    disabled={!calculatorData.squareFootage}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Calculate Cost
                  </button>
                </div>

                {/* Results */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Cost Estimate</h4>
                  
                  {calculationResult ? (
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600 mb-1">
                            {formatCurrency(calculationResult.total)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatCurrency(calculationResult.costPerSqft)} per sq ft
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-gray-600">Base Construction Cost</span>
                          <span className="font-medium">{formatCurrency(calculationResult.baseCost)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-gray-600">Additional Features</span>
                          <span className="font-medium">{formatCurrency(calculationResult.additionalFeatures)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200 font-semibold">
                          <span className="text-gray-900">Total Estimated Cost</span>
                          <span className="text-blue-600">{formatCurrency(calculationResult.total)}</span>
                        </div>
                      </div>

                      <div className="mt-6">
                        <h5 className="font-medium text-gray-900 mb-3">Cost Breakdown</h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Labor (40%)</span>
                            <span>{formatCurrency(calculationResult.laborCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Materials (45%)</span>
                            <span>{formatCurrency(calculationResult.materialCost)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Overhead & Profit (15%)</span>
                            <span>{formatCurrency(calculationResult.overhead)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                          <i className="ri-information-line text-yellow-600 mr-2 mt-0.5"></i>
                          <div className="text-sm text-yellow-800">
                            <p className="font-medium mb-1">Estimate Disclaimer</p>
                            <p>This is a rough estimate based on average costs. Actual costs may vary significantly based on location, materials, labor rates, and project complexity. Consult with contractors for accurate quotes.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <i className="ri-calculator-line text-4xl text-gray-300 mb-4"></i>
                      <p className="text-gray-500">Enter project details and click "Calculate Cost" to see your estimate</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Misc;
